import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Mail } from 'lucide-react'
import { api } from '@/lib/api'
import { formatDateTime } from '@/lib/date'
import type { EmailLogRow } from '@/types'
import PageHeader from '@/components/ui/PageHeader'
import Badge, { type BadgeTone } from '@/components/ui/Badge'
import Modal from '@/components/ui/Modal'
import { Table, Row, Cell, EmptyRow } from '@/components/ui/Table'
import LogoLoader from '@/components/ui/LogoLoader'

const typeTones: Record<string, BadgeTone> = {
  order_confirmation: 'brand',
  order_rescheduled: 'warning',
  order_cancelled: 'danger',
  topup_receipt: 'info',
  wallet_adjustment: 'info',
  welcome: 'success',
  staff_invite: 'success',
  partner_invite: 'success',
  password_reset: 'neutral',
  account_suspended: 'danger',
}

export default function AdminEmailLog() {
  const [selected, setSelected] = useState<EmailLogRow | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['email-logs'],
    queryFn: async () => (await api.get<{ data: EmailLogRow[] }>('/admin/email-logs')).data,
  })

  if (isLoading) return <LogoLoader />

  return (
    <div>
      <PageHeader
        title="Email log"
        subtitle="Every automated email the system sends. Links are left out, so a password link here cannot be used"
      />

      <Table columns={['Sent', 'To', 'Type', 'Subject']}>
        {(data?.data.length ?? 0) === 0 && <EmptyRow colSpan={4} />}
        {data?.data.map((log) => (
          <Row key={log.id} onClick={() => setSelected(log)}>
            <Cell className="whitespace-nowrap text-xs text-muted">{formatDateTime(log.created_at)}</Cell>
            <Cell className="text-xs">{log.to}</Cell>
            <Cell>
              <Badge tone={typeTones[log.type] ?? 'brand'}>{log.type.replace(/_/g, ' ')}</Badge>
            </Cell>
            <Cell className="font-semibold">{log.subject}</Cell>
          </Row>
        ))}
      </Table>

      <Modal open={Boolean(selected)} onClose={() => setSelected(null)} title={selected?.subject}>
        <div className="space-y-3 text-sm">
          <p className="flex flex-wrap items-center gap-2 text-xs text-muted">
            <Mail className="size-4" /> To: {selected?.to} - {selected && formatDateTime(selected.created_at)}
          </p>
          <p className="whitespace-pre-line break-words rounded-lg bg-app p-4 leading-relaxed">{selected?.body}</p>
        </div>
      </Modal>
    </div>
  )
}
